import { __spreadArrays } from "tslib";
import { DisjunctiveAvailabilityIterator, ConjunctiveTimeWindowsIterator, AvailabilityIterator, isAvailableOn, intersectWeeklyAvailabilities, } from 'availability';
import moment from 'moment-timezone';
import _ from 'lodash';
import StatusHelper from '@wix/wixrest-utils/dist/openrest-helpers/Status';
import { unionAvailabilityExceptions, unionWeeklyAvailabilities, } from './unionAvailabilites';
import { getDelayMins } from './restaurantLogic';
import { simpleMemoize } from './simpleMemoize';
import { BusinessNotifications } from '../types/BusinessNotifications';
export function getAvailableTimingOptions(restaurant, dispatchType, dispatchTime, idealDeliveryArea, businessNotification) {
    var _a, _b, _c, _d;
    var timezone = restaurant.timezone;
    var initialTime = moment(dispatchTime).tz(timezone);
    var numberOfDays = ((_a = restaurant.orders.future.delayMins) === null || _a === void 0 ? void 0 : _a.max) / 1440;
    var finalAvailability = getFinalAvailability(restaurant, dispatchType, idealDeliveryArea);
    var dateOptions = getDateOptions({
        availability: finalAvailability,
        timezone: timezone,
        numberOfDays: numberOfDays,
        delayMins: restaurant.orders.future.delayMins,
    });
    var selectedDate = dispatchTime
        ? initialTime.startOf('day').valueOf()
        : (_b = dateOptions[0]) === null || _b === void 0 ? void 0 : _b.timestamp;
    var earliestTimeOptions = ((_c = dateOptions[0]) === null || _c === void 0 ? void 0 : _c.timestamp) ? getTimeOptions({
        availability: finalAvailability,
        day: selectedDate,
        timezone: timezone,
        delayMins: restaurant.orders.future.delayMins,
    })
        : [];
    var isOnlyAsap = restaurant.orders.future.disabled && !restaurant.orders.asap.disabled;
    var isOnlyFutureOrders = dispatchType === 'delivery'
        ? businessNotification &&
            businessNotification === BusinessNotifications.OnlyFutureOrders
        : !restaurant.orders.future.disabled && ((_d = earliestTimeOptions[0]) === null || _d === void 0 ? void 0 : _d.timestamp) &&
            moment(earliestTimeOptions[0].timestamp).tz(timezone) >
                moment()
                    .tz(timezone)
                    .add(restaurant.orders.future.delayMins.min + 15, 'minutes');
    var isAsapDisabled = Boolean(restaurant.orders.asap.disabled);
    var result = ['asap', 'later'];
    if (isOnlyAsap) {
        result = result.filter(function (x) { return x === 'asap'; });
    }
    if (isOnlyFutureOrders) {
        result = result.filter(function (x) { return x === 'later'; });
    }
    if (isAsapDisabled) {
        result = result.filter(function (x) { return x !== 'asap'; });
    }
    return result;
}
export function getAvailableDeliveryInfos(deliveryInfos, timezone) {
    return deliveryInfos.filter(function (deliveryInfo) {
        var _a = StatusHelper.getStatus(deliveryInfo.availability, moment().tz(timezone)), status = _a.status, reason = _a.reason;
        var isClosed = status === 'unavailable' && reason === 'closed';
        return deliveryInfo.inactive !== true && !isClosed;
    });
}
export function getAsapParams(_a) {
    var idealDeliveryArea = _a.idealDeliveryArea, deliveryInfos = _a.deliveryInfos, dispatchType = _a.dispatchType, timezone = _a.timezone;
    var availableDeliveryInfos = getAvailableDeliveryInfos(deliveryInfos, timezone);
    return dispatchType === 'delivery'
        ? getAsapParamsDelivery(availableDeliveryInfos, idealDeliveryArea)
        : getAsapParamsTakeout(availableDeliveryInfos);
}
export function getAsapParamsTakeout(deliveryInfos) {
    var takeout = _.find(deliveryInfos, function (info) { return info.type === 'takeout'; });
    var type, value;
    if (!takeout) {
        type = 'invalid';
    }
    else {
        type = takeout.delayMins !== 0 ? 'exact' : 'immediate';
        value = takeout.delayMins;
    }
    return { type: type, value: value };
}
export function getAsapParamsDelivery(deliveryInfos, idealDeliveryArea) {
    var delayMins = idealDeliveryArea
        ? [idealDeliveryArea.delayMins || 0]
        : getDelayMins({ deliveryInfos: deliveryInfos, dispatchType: 'delivery' });
    var type;
    if (delayMins.length === 0) {
        return { type: 'unavailable' };
    }
    else if (delayMins.length === 1) {
        type = delayMins[0] === 0 ? 'immediate' : 'exact';
        return { type: type, value: delayMins[0] };
    }
    else {
        var min = _.min(delayMins);
        var max = _.max(delayMins);
        if (min === 0) {
            return { type: 'exact', value: max };
        }
        else {
            return { type: 'range', min: min, max: max };
        }
    }
}
var _isAvailableOn = simpleMemoize(function (availability, time) {
    return isAvailableOn(availability, time);
});
export var isAvailabilityIteratorAvailable = function (availabilityIterator) {
    return availabilityIterator.hasNext()
        ? availabilityIterator.next().status === 'available'
        : false;
};
function isAvailableSomeTimeOnDay(availability, day, timezone, delayMins, dishPrepareTime, availabilityIteratorFactory) {
    var timeOptions = getTimeOptions({
        availability: availability,
        availabilityIteratorFactory: availabilityIteratorFactory,
        day: day,
        timezone: timezone,
        delayMins: delayMins,
        dishPrepareTime: dishPrepareTime,
    });
    return (timeOptions.length > 0 &&
        getDaysDiff(timeOptions[0].timestamp, timezone, day) === 0);
}
function getDateOptionsV2(availabilityIteratorFactory, startDate, numberOfDays, timezone, minimalWaitTime, delayMins) {
    var iterator = availabilityIteratorFactory(startDate);
    var result = [];
    var currentDate = startDate.clone();
    while (iterator.hasNext() &&
        currentDate.diff(startDate, 'day') < numberOfDays) {
        var _a = iterator.next(), status_1 = _a.status, until = _a.until;
        var untilDate = until
            ? moment(until).tz(timezone)
            : startDate.clone().add(numberOfDays, 'day');
        if (status_1 === 'available') {
            var diff = untilDate.diff(currentDate, 'day');
            var _loop_1 = function (i) {
                var day = currentDate.clone().add(i, 'days');
                if (delayMins &&
                    day.diff(startDate, 'minutes') > delayMins.max - minimalWaitTime) {
                    return { value: result };
                }
                var newDisplayValue = day.clone().startOf('day').format('MMMM Do');
                if (!result.find(function (_a) {
                    var displayableValue = _a.displayableValue;
                    return displayableValue === newDisplayValue;
                })) {
                    result.push({
                        timestamp: day.clone().startOf('day').toDate().getTime(),
                        displayableValue: newDisplayValue,
                    });
                }
                if (diff === 0) {
                    return "break";
                }
            };
            for (var i = 0; i < diff || diff === 0; i++) {
                var state_1 = _loop_1(i);
                if (typeof state_1 === "object")
                    return state_1.value;
                if (state_1 === "break")
                    break;
            }
        }
        currentDate = untilDate;
    }
    return result;
}
export var getDateOptions = simpleMemoize(function (_a) {
    var _b = _a.numberOfDays, numberOfDays = _b === void 0 ? 7 : _b, _c = _a.availability, availability = _c === void 0 ? {} : _c, availabilityIteratorFactory = _a.availabilityIteratorFactory, timezone = _a.timezone, delayMins = _a.delayMins, dishPrepareTime = _a.dishPrepareTime;
    var result = [];
    var minimalWaitTime = Math.max((delayMins === null || delayMins === void 0 ? void 0 : delayMins.min) || 0, dishPrepareTime || 0);
    var startDate = moment()
        .tz(timezone)
        .add(minimalWaitTime, 'minutes')
        .startOf('day');
    if (availabilityIteratorFactory) {
        return getDateOptionsV2(availabilityIteratorFactory, startDate.clone(), numberOfDays, timezone, minimalWaitTime, delayMins);
    }
    for (var i = 0; i < numberOfDays; i++) {
        var day = startDate.clone().add(i, 'days');
        if (delayMins &&
            day.diff(startDate, 'minutes') > delayMins.max - minimalWaitTime) {
            break;
        }
        if (isAvailableSomeTimeOnDay(availability, day.valueOf(), timezone, delayMins, dishPrepareTime, availabilityIteratorFactory)) {
            result.push({
                timestamp: day.toDate().getTime(),
                displayableValue: day.format('MMMM Do'),
            });
        }
    }
    return result;
});
export var getTimeOptions = simpleMemoize(function (_a) {
    var day = _a.day, timezone = _a.timezone, _b = _a.locale, locale = _b === void 0 ? '' : _b, _c = _a.availability, availability = _c === void 0 ? {} : _c, availabilityIteratorFactory = _a.availabilityIteratorFactory, delayMins = _a.delayMins, dishPrepareTime = _a.dishPrepareTime;
    var result = [];
    var earliest = moment()
        .tz(timezone)
        .add(Math.max((delayMins === null || delayMins === void 0 ? void 0 : delayMins.min) || 0, dishPrepareTime || 0), 'minutes');
    var isAtEarliestDay = getDaysDiff(day, timezone, earliest.valueOf()) === 0;
    var anchor = isAtEarliestDay
        ? getRoundedMinutesValue(earliest)
        : moment(day).tz(timezone).startOf('day');
    var endOfDay = anchor.clone().endOf('day');
    var endAnchor = delayMins
        ? moment.min(moment().tz(timezone).add(delayMins.max, 'minutes'), endOfDay)
        : endOfDay;
    while (anchor.valueOf() < endAnchor.valueOf()) {
        var isAvailable = availabilityIteratorFactory
            ? isAvailabilityIteratorAvailable(availabilityIteratorFactory(anchor))
            : _isAvailableOn(availability, anchor);
        if (isAvailable) {
            result.push({
                timestamp: anchor.valueOf(),
                displayableValue: anchor.locale(locale).format('LT'),
            });
        }
        anchor.add(15, 'minutes');
    }
    return result;
});
function getRoundedMinutesValue(m, interval) {
    if (interval === void 0) { interval = 15; }
    if (interval <= 0 || interval > 60) {
        throw new Error("Invalid interval " + interval);
    }
    var roundedMinutes = Math.ceil(m.minute() / interval) * interval;
    return m.clone().minute(roundedMinutes).seconds(0).milliseconds(0);
}
export function getDaysDiff(timestamp, timezone, comparedTo) {
    return moment(timestamp)
        .tz(timezone)
        .startOf('day')
        .diff(moment(comparedTo).tz(timezone).startOf('day'), 'day');
}
function getAvailabilitiesByDispatchType(restaurant, dispatchType) {
    var deliveryInfos = getAvailableDeliveryInfos(restaurant.deliveryInfos, restaurant.timezone);
    return deliveryInfos
        .filter(function (di) { return di.type === dispatchType; })
        .map(function (di) { return di.availability; });
}
export function getFinalAvailability(restaurant, dispatchType, selectedDeliveryAreas, includeExceptions) {
    var availabilities = selectedDeliveryAreas && dispatchType === 'delivery'
        ? []
            .concat(selectedDeliveryAreas)
            .map(function (di) { return di.availability; })
        : getAvailabilitiesByDispatchType(restaurant, dispatchType);
    var weekly = intersectWeeklyAvailabilities(restaurant.openTimes, unionWeeklyAvailabilities(availabilities)).weekly;
    var exceptions = unionAvailabilityExceptions(availabilities);
    var filteredExceptions = __spreadArrays(exceptions.filter(function (e) { return e.available || includeExceptions; }), (restaurant.openTimes.exceptions || []));
    var result = {
        weekly: (weekly || []).filter(function (x) { return x.durationMins > 0; }),
    };
    if (filteredExceptions.length > 0) {
        result.exceptions = filteredExceptions;
    }
    return result;
}
export function getFinalAvailabilityIteratorFactory(restaurant, dispatchType, selectedDeliveryAreas) {
    var availabilities = selectedDeliveryAreas && dispatchType === 'delivery'
        ? []
            .concat(selectedDeliveryAreas)
            .map(function (di) { return di.availability; })
        : getAvailabilitiesByDispatchType(restaurant, dispatchType);
    var conjunctIter = function (cal) {
        return ConjunctiveTimeWindowsIterator({
            cal: cal,
            iterators: [
                new DisjunctiveAvailabilityIterator({
                    availabilities: availabilities,
                    cal: cal,
                }),
                new AvailabilityIterator({
                    availability: restaurant.openTimes,
                    cal: cal,
                }),
            ],
        });
    };
    return conjunctIter;
}
//# sourceMappingURL=dateTimeLogic.js.map